import React, { useState, useCallback, useEffect } from 'react';
import { Users, Flag, Dumbbell, Trophy, ChevronRight, X, MessageCircle } from 'lucide-react';

const CARDS = [
  {
    icon: Users,
    color: 'var(--club-green)',
    title: 'Find Playing Partners',
    body: 'Swipe to discover golfers near you. Match by skill level, location, and availability. Chat in-app to plan your next round.',
    tip: 'Head to the Discover tab to start swiping and connecting.',
  },
  {
    icon: Flag,
    color: 'var(--emerald)',
    title: 'Track Your Scores',
    body: 'Log your rounds with a simple or detailed scorecard. Track your putts, fairways, greens in regulation, and watch your game improve over time.',
    tip: 'Tap the Scores tab to log a round or view your history.',
  },
  {
    icon: Dumbbell,
    color: 'var(--burgundy)',
    title: 'Social Feed',
    body: 'Share your rounds, workouts, and golf moments with the community. Follow other golfers and stay connected.',
    tip: 'Visit the Feed tab to see what others are sharing.',
  },
  {
    icon: Trophy,
    color: 'var(--gold)',
    title: 'Message Your Matches',
    body: 'When you match with a golfer, start a conversation to plan your next round. Coordinate tee times and get to know each other.',
    tip: 'Check the Messages tab to chat with your matches.',
  },
];

const OnboardingCards = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const finish = useCallback(() => {
    setExiting(true);
    localStorage.setItem('club_onboarding_done', 'true');
    setTimeout(() => onComplete(), 400);
  }, [onComplete]);

  const next = () => {
    if (current === CARDS.length - 1) { finish(); return; }
    setDirection(1);
    setCurrent(c => c + 1);
  };

  const card = CARDS[current];

  return (
    <div
      className="fixed inset-0 z-[90] flex flex-col items-center justify-center px-6 transition-opacity duration-400"
      style={{
        background: 'var(--club-cream)',
        opacity: exiting ? 0 : 1,
      }}
      data-testid="onboarding-cards"
    >
      {/* Skip */}
      <button
        onClick={finish}
        className="absolute top-5 right-5 flex items-center gap-1 text-sm font-medium opacity-50 hover:opacity-80 transition-opacity"
        style={{ color: 'var(--warm-gray)' }}
        data-testid="onboarding-skip"
      >
        Skip <X className="w-4 h-4" />
      </button>

      {/* Card content */}
      <div className="w-full max-w-sm text-center" key={current}>
        {/* Icon circle */}
        <div
          className="w-20 h-20 rounded-2xl mx-auto mb-8 flex items-center justify-center animate-fade-in"
          style={{ background: `${card.color}12` }}
        >
          <card.icon className="w-10 h-10" style={{ color: card.color }} />
        </div>

        {/* Title */}
        <h2
          className="font-serif text-3xl font-bold mb-4 animate-slide-up"
          style={{ color: 'var(--deep-forest)' }}
        >
          {card.title}
        </h2>

        {/* Body */}
        <p
          className="text-sm leading-relaxed mb-3 animate-slide-up"
          style={{ color: 'var(--warm-gray)', animationDelay: '60ms' }}
        >
          {card.body}
        </p>

        {/* Tip */}
        <div
          className="inline-block px-4 py-2 rounded-full text-xs font-semibold animate-slide-up"
          style={{
            background: 'var(--fairway-sand)',
            color: 'var(--club-green)',
            animationDelay: '120ms',
          }}
        >
          {card.tip}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-12 left-0 right-0 px-6">
        {/* Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {CARDS.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === current ? '24px' : '8px',
                background: i === current ? 'var(--club-green)' : 'var(--stone)',
              }}
            />
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={next}
          className="w-full max-w-sm mx-auto h-14 rounded-xl flex items-center justify-center gap-2 font-semibold text-sm tracking-wide transition-all btn-press"
          style={{ background: 'var(--club-green)', color: '#fff' }}
          data-testid="onboarding-next"
        >
          {current === CARDS.length - 1 ? "Let's Go" : 'Next'}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default OnboardingCards;
